'use client';

import loadingImage from '@/assets/images/common/loading-100.gif';
import { NEXT_IMAGE_PLACEHOLDER } from '@/constants/next-image-placeholder';
import { GET_MINI_CART_FREQUENTLY_BOUGHT_PRODUCTS } from '@/graphql/queries/get-mini-cart-frequently-bought-products';
import { GET_MINI_CART_FREQUENTLY_BOUGHT_URL_KEY } from '@/graphql/queries/get-mini-cart-frequently-bought-url-key';
import useIsMobile from '@/hooks/use-is-mobile';
import { useGetCategoryFinalPriceAndCrossPrice } from '@/hooks/useGetCategoryPriceAndCrossPrice';
import { useLazyQuery } from '@apollo/client';
import clsx from 'clsx';
import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';
import Link from 'next/link';
import { createContext, forwardRef, useCallback, useContext, useEffect, useState } from 'react';
import { extendTailwindMerge } from 'tailwind-merge';
import Price from '../Price';

const cn = extendTailwindMerge({
  separator: '_',
});
let prevLastAddToCartTimestamp = '';
let shoesUrlKeys = null;
let robesUrlKeys = null;
let giftsUrlKeys = null;
let lastTimeRandomProducts = [];

const RandomRelatedProducts = props => {
  const { lastAddToCartTimestamp, onCloseMiniCart } = props;
  const isMobile = useIsMobile();
  const [reqGetFrequentlyBoughtUrlKeys, { loading: urlKeyLoading }] = useLazyQuery(
    GET_MINI_CART_FREQUENTLY_BOUGHT_URL_KEY,
    {
      fetchPolicy: 'no-cache',
    },
  );
  const [reqGetFrequentlyBoughtProducts, { loading: productLoading }] = useLazyQuery(
    GET_MINI_CART_FREQUENTLY_BOUGHT_PRODUCTS,
    {
      fetchPolicy: 'no-cache',
    },
  );
  const [products, setProducts] = useState(lastTimeRandomProducts);

  useEffect(() => {
    if (!lastAddToCartTimestamp || prevLastAddToCartTimestamp === lastAddToCartTimestamp) {
      return;
    }

    prevLastAddToCartTimestamp = lastAddToCartTimestamp;

    const getRandomProducts = async () => {
      if (shoesUrlKeys === null) {
        const urlKeyRes = await reqGetFrequentlyBoughtUrlKeys()
          .then(({ data }) => data)
          .catch(() => null);

        if (urlKeyRes === null) {
          return;
        }

        shoesUrlKeys = urlKeyRes?.shoes?.items?.map(x => x.url_key) || [];
        robesUrlKeys = urlKeyRes?.robes?.items?.map(x => x.url_key) || [];
        giftsUrlKeys = urlKeyRes?.gifts?.items?.map(x => x.url_key) || [];
      }

      reqGetFrequentlyBoughtProducts({
        variables: {
          urlKeys: [
            ...generateRandomUrlKey(shoesUrlKeys, 4),
            ...generateRandomUrlKey(robesUrlKeys, 4),
            ...generateRandomUrlKey(giftsUrlKeys, 4),
          ],
        },
      }).then(({ data }) => {
        const products = data?.products?.items || [];

        setProducts(products);
        lastTimeRandomProducts = products;
      });
    };

    getRandomProducts();
  }, [lastAddToCartTimestamp, reqGetFrequentlyBoughtUrlKeys, reqGetFrequentlyBoughtProducts]);

  if (urlKeyLoading || productLoading) {
    return (
      <div className='flex min-h-[300px] items-center justify-center'>
        <Image
          width={100}
          height={100}
          src={loadingImage}
          alt=''
        />
      </div>
    );
  }

  if (!Array.isArray(products) || !products.length) {
    return null;
  }

  return (
    <div className='mt-[25px]'>
      <div className='text-[17px] font-medium leading-5'>Frequently Bought Together</div>
      {isMobile ? (
        <div
          className='mt-2.5 overflow-auto'
          id='frequently-bought-together-container'
        >
          <div className='flex items-center gap-x-[7px] pb-5 lg_gap-x-2.5'>
            {products.map(x => {
              if (!x) {
                return null;
              }

              return (
                <RandomRelatedProduct
                  key={x.id}
                  product={x}
                  onCloseMiniCart={onCloseMiniCart}
                />
              );
            })}
          </div>
        </div>
      ) : (
        <ProductsCarousel
          products={products}
          onCloseMiniCart={onCloseMiniCart}
        />
      )}
    </div>
  );
};

const ProductsCarousel = props => {
  const { products, onCloseMiniCart } = props;

  return (
    <Carousel
      className='mt-2.5'
      opts={{ align: 'start' }}
    >
      <CarouselContent className='-ml-[7px] lg_-ml-2.5'>
        {products.map(x => {
          if (!x) {
            return null;
          }

          return (
            <CarouselItem
              key={x.id}
              className='basis-[143px] pl-[7px] lg_basis-[175px] lg_pl-2.5'
            >
              <RandomRelatedProduct
                key={x.id}
                product={x}
                onCloseMiniCart={onCloseMiniCart}
              />
            </CarouselItem>
          );
        })}
      </CarouselContent>
      <CarouselScrollBar />
    </Carousel>
  );
};

const CarouselScrollBar = () => {
  const { canScrollNext, canScrollPrev, api } = useCarousel();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const count = api?.scrollSnapList() || [];

  useEffect(() => {
    if (!api) {
      return;
    }

    const selectHandler = () => {
      setSelectedIndex(api?.selectedScrollSnap() || 0);
    };

    api.on('select', selectHandler);

    return () => {
      api.off('select', selectHandler);
    };
  }, [api]);

  return canScrollPrev || canScrollNext ? (
    <div className='relative mt-5'>
      <div
        className='absolute top-0 h-1.5 w-[165px] bg-E7DED5'
        style={{
          left: `${count[selectedIndex] * 100 || 0}%`,
          transform: `translateX(-${count[selectedIndex] * 100 || 0}%)`,
        }}
      ></div>
    </div>
  ) : null;
};

const RandomRelatedProduct = props => {
  const { product, onCloseMiniCart } = props;
  const { version } = product;

  const baseImageUrl = generateImageUrl(product.image?.url, version);
  const baseHoverImageUrl = generateImageUrl(product.hover_image, version);
  const decodedProductName = product.name?.replace(/&quot;/g, '"') || '';
  const { finalPrice, currency } = useGetCategoryFinalPriceAndCrossPrice({
    product,
  });

  return (
    <div className='w-[136px] flex-none lg_w-[165px]'>
      <Link
        href={`${product.url_key}.html`}
        className='group relative block text-[0]'
        onClick={onCloseMiniCart}
      >
        {baseImageUrl ? (
          <Image
            width={165}
            height={241}
            src={baseImageUrl}
            alt={decodedProductName}
            className={clsx(
              'h-[200px] w-[136px] object-cover lg_h-[241px] lg_w-[165px]',
              baseHoverImageUrl ? 'transition-opacity duration-4xl ease-linear lg_group-hover_opacity-0' : '',
            )}
            placeholder={NEXT_IMAGE_PLACEHOLDER}
          />
        ) : null}
        {baseHoverImageUrl ? (
          <Image
            width={165}
            height={241}
            src={baseHoverImageUrl}
            alt={decodedProductName}
            className={clsx(
              'h-[200px] w-[136px] object-cover lg_h-[241px] lg_w-[165px]',
              baseHoverImageUrl
                ? 'absolute left-0 top-0 z-[1] opacity-0 transition-opacity duration-4xl ease-linear lg_group-hover_opacity-100'
                : '',
            )}
            placeholder={NEXT_IMAGE_PLACEHOLDER}
          />
        ) : null}
      </Link>
      <Link
        href={`${product.url_key}.html`}
        className='mt-[5px] block truncate text-sm leading-5'
        title={decodedProductName}
        onClick={onCloseMiniCart}
      >
        {decodedProductName}
      </Link>
      <div className='text-sm leading-5'>
        <Price
          value={finalPrice}
          currencyCode={currency}
        />
      </div>
    </div>
  );
};

const generateImageUrl = (baseUrl, version) => {
  if (!baseUrl) {
    return '';
  }

  if (!version) {
    return baseUrl;
  }

  const urlInstance = new URL(baseUrl);

  if (urlInstance.search) {
    urlInstance.search = `${urlInstance.search}&version=${version}`;
  } else {
    urlInstance.search = `?version=${version}`;
  }

  return urlInstance.toString();
};

const generateRandomUrlKey = (urlKeys, count) => {
  const urlKeysCopy = urlKeys.slice(0);
  const ret = [];

  if (urlKeysCopy.length <= count) {
    return urlKeysCopy;
  }

  while (ret.length < count) {
    const randomIndex = Math.floor(Math.random() * urlKeysCopy.length);

    ret.push(urlKeysCopy.splice(randomIndex, 1)[0]);
  }

  return ret;
};

const CarouselContext = createContext(null);

function useCarousel() {
  const context = useContext(CarouselContext);

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }

  return context;
}

const Carousel = forwardRef(
  ({ orientation = 'horizontal', opts, setApi, plugins, className, children, ...props }, ref) => {
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === 'horizontal' ? 'x' : 'y',
      },
      plugins,
    );
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    const onSelect = useCallback(api => {
      if (!api) {
        return;
      }

      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    }, []);

    const scrollPrev = useCallback(() => {
      api?.scrollPrev();
    }, [api]);

    const scrollNext = useCallback(() => {
      api?.scrollNext();
    }, [api]);

    const handleKeyDown = useCallback(
      event => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          scrollNext();
        }
      },
      [scrollPrev, scrollNext],
    );

    useEffect(() => {
      if (!api || !setApi) {
        return;
      }

      setApi(api);
    }, [api, setApi]);

    useEffect(() => {
      if (!api) {
        return;
      }

      onSelect(api);
      api.on('reInit', onSelect);
      api.on('select', onSelect);

      return () => {
        api?.off('select', onSelect);
      };
    }, [api, onSelect]);

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api: api,
          opts,
          orientation: orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
        }}
      >
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn('relative', className)}
          role='region'
          aria-roledescription='carousel'
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    );
  },
);

Carousel.displayName = 'Carousel';

const CarouselContent = forwardRef(({ className, ...props }, ref) => {
  const { carouselRef } = useCarousel();

  return (
    <div
      ref={carouselRef}
      className='overflow-hidden'
    >
      <div
        ref={ref}
        className={cn('flex', className)}
        {...props}
      />
    </div>
  );
});

CarouselContent.displayName = 'CarouselContent';

const CarouselItem = forwardRef(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      role='group'
      aria-roledescription='slide'
      className={cn('min-w-0 shrink-0 grow-0', className)}
      {...props}
    />
  );
});

CarouselItem.displayName = 'CarouselItem';

export default RandomRelatedProducts;
